import { setActiveSpecificationItem } from "@/core/redux/slices/specification";
import { useDispatch, useSelector } from "@/core/redux/store";
import {
  ISpecificationCategoryResponse,
  ISpecificationItemResponseExtended,
} from "@/shared/services/interface/responses/ISpecificationResponse";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Circle, CircleCheck, CircleX } from "lucide-react";
import CakisanIcon from "@/assets/icons/cakisan.svg";
import BelkiIcon from "@/assets/icons/belki.svg";
import BulunamadiIcon from "@/assets/icons/bulunamadi.svg";
import { cn } from "@/lib/utils";
import { SpecificationItemAnswerStatus } from "@/shared/enums/specification-item-answer";
import { ISurveyAnswer, ISupplierCategory, ISupplierCategoryItem } from "@/shared/services/interface/responses/suplier-response";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

const getLeafBgClasses = ({
  isActive,
  hasConflict,
  status,
}: {
  isActive: boolean;
  hasConflict: boolean;
  status: SpecificationItemAnswerStatus | null;
}) => {
  // Default behavior:
  // - Active leaf has neutral selection background
  // - Non-active leaf uses status-based background (when answered)
  if (isActive) return "bg-[#F4F4F5]";

  if (hasConflict) {
    return "bg-[#FFF7ED] hover:brightness-[0.98]";
  }

  switch (status) {
    case SpecificationItemAnswerStatus.ANSWERED_YES:
      return "bg-[#ECFDF5] hover:brightness-[0.98]";
    case SpecificationItemAnswerStatus.ANSWERED_NO:
      return "bg-[#FEF2F2] hover:brightness-[0.98]";
    case SpecificationItemAnswerStatus.ANSWERED_PARTIALLY:
      return "bg-[#FFF7ED] hover:brightness-[0.98]";
    case SpecificationItemAnswerStatus.NOT_FOUND:
      return "bg-[#F4F4F5] hover:brightness-[0.98]";
    default:
      return "hover:bg-[#F4F4F5]";
  }
};

const normalizeAnswerStatus = (
  status: unknown
): SpecificationItemAnswerStatus | null => {
  // Backend / older payloads may send status as a string; keep UI resilient.
  if (typeof status === "number") {
    if (
      status === SpecificationItemAnswerStatus.SEARCHING ||
      status === SpecificationItemAnswerStatus.ANSWERED_PARTIALLY ||
      status === SpecificationItemAnswerStatus.ANSWERED_YES ||
      status === SpecificationItemAnswerStatus.ANSWERED_NO ||
      status === SpecificationItemAnswerStatus.NOT_FOUND
    ) {
      return status;
    }
    return null;
  }

  if (typeof status === "string") {
    const trimmed = status.trim();
    if (!trimmed) return null;

    const asNumber = Number(trimmed);
    if (!Number.isNaN(asNumber)) return normalizeAnswerStatus(asNumber);

    const key = trimmed.toUpperCase();
    switch (key) {
      case "SEARCHING":
        return SpecificationItemAnswerStatus.SEARCHING;
      case "ANSWERED_PARTIALLY":
      case "PARTIALLY":
        return SpecificationItemAnswerStatus.ANSWERED_PARTIALLY;
      case "ANSWERED_YES":
      case "YES":
        return SpecificationItemAnswerStatus.ANSWERED_YES;
      case "ANSWERED_NO":
      case "NO":
        return SpecificationItemAnswerStatus.ANSWERED_NO;
      case "NOT_FOUND":
      case "NOTFOUND":
        return SpecificationItemAnswerStatus.NOT_FOUND;
      default:
        return null;
    }
  }

  return null;
};

// Custom expand icon matching Figma design - two horizontal bars in a rounded container (16x16)
const ExpandIcon = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "flex flex-col items-center justify-center w-4 h-4 rounded-[4px] gap-[3px] flex-shrink-0 cursor-pointer hover:bg-[#E4E4E7] transition-colors duration-150",
      className
    )}
  >
    <div className="w-[8px] h-0 border-t border-zinc-500" />
    <div className="w-[8px] h-0 border-t border-zinc-500" />
  </div>
);

// Helper function to find surveyAnswer for an item from nested categories
const findSurveyAnswerForItem = (
  categories: ISupplierCategory[] | undefined,
  itemId: string
): ISurveyAnswer | null => {
  if (!categories) return null;
  
  for (const category of categories) {
    for (const child of category.children) {
      if (child.type === "item" && child.id === itemId) {
        return (child as ISupplierCategoryItem).surveyAnswer;
      }
      // Check nested categories
      if (child.type === "category") {
        const nestedResult = findSurveyAnswerForItem([child as ISupplierCategory], itemId);
        if (nestedResult !== null) return nestedResult;
      }
    }
  }
  return null;
};

// Survey answer status circle component - matches Figma design (Lucide icons 12x12)
const SurveyAnswerCircle = ({ surveyAnswer, isActive, hasConflict }: { surveyAnswer: ISurveyAnswer | null; isActive: boolean; hasConflict: boolean }) => {
  // If there's a conflict (supplier and AI disagree), always show cakisan icon
  if (hasConflict) {
    return (
      <img src={CakisanIcon} alt="conflict" width={12} height={12} className="flex-shrink-0" />
    );
  }

  const normalizedStatus = normalizeAnswerStatus(surveyAnswer?.status);

  // No answer / unanswered
  if (!surveyAnswer || normalizedStatus === null) {
    return (
      <Circle size={12} className={cn("flex-shrink-0", isActive ? "text-[#18181B]" : "text-[#71717A]")} strokeWidth={2} />
    );
  }

  // YES - circle-check icon
  if (normalizedStatus === SpecificationItemAnswerStatus.ANSWERED_YES) {
    return (
      <CircleCheck
        aria-label="yes"
        size={12}
        className="flex-shrink-0 text-[#16A34A]"
        strokeWidth={2}
      />
    );
  }

  // NO - circle-x icon
  if (normalizedStatus === SpecificationItemAnswerStatus.ANSWERED_NO) {
    return (
      <CircleX
        aria-label="no"
        size={12}
        className="flex-shrink-0 text-[#DC2626]"
        strokeWidth={2}
      />
    );
  }

  // PARTIALLY - orange circle with check
  if (normalizedStatus === SpecificationItemAnswerStatus.ANSWERED_PARTIALLY) {
    return <img src={BelkiIcon} alt="partially" width={12} height={12} className="flex-shrink-0" />;
  }

  // Searching - unanswered state
  if (normalizedStatus === SpecificationItemAnswerStatus.SEARCHING) {
    return (
      <Circle size={12} className={cn("flex-shrink-0", isActive ? "text-[#18181B]" : "text-[#71717A]")} strokeWidth={2} />
    );
  }

  // Not found - gray circle with exclamation
  if (normalizedStatus === SpecificationItemAnswerStatus.NOT_FOUND) {
    return <img src={BulunamadiIcon} alt="not found" width={12} height={12} className="flex-shrink-0" />;
  }

  // Default - unanswered
  return (
    <Circle size={12} className={cn("flex-shrink-0", isActive ? "text-[#18181B]" : "text-[#71717A]")} strokeWidth={2} />
  );
};

export const CategoryItem = ({
  subItem,
  sidebarWidth,
  priority,
  subIndex,
  item,
}: {
  subItem: ISpecificationItemResponseExtended;
  sidebarWidth: number;
  priority: string;
  subIndex: number;
  item: ISpecificationCategoryResponse;
}) => {
  const specification = useSelector((state) => state.specification);
  const activeSpecificationStep = specification.activeSpecificationStep;
  const dispatch = useDispatch();

  // Get supplier categories with survey answers
  const supplierCategoriesResponse = useSelector(
    (state) => state.productAnswer.supplierCategoriesResponse
  );

  // Get AI answers to detect conflicts
  const aiAnswers = useSelector(
    (state) => state.productAnswer.supplierAndAIAnswersResponse?.specificationItemAnswers
  );
  const supplierSurveys = useSelector(
    (state) => state.productAnswer.supplierAndAIAnswersResponse?.supplierSurveys
  );
  const transientSelectedAnswer = useSelector(
    (state) => state.productAnswer.transientSelectedAnswer
  );

  // Prefer the latest UI/Redux states so sidebar is updated immediately after selection.
  const surveyAnswer = useMemo(() => {
    if (transientSelectedAnswer?.specificationItemId === subItem.id) {
      return {
        status: transientSelectedAnswer.status,
        description: "",
      };
    }

    const surveyFromAnswersEndpoint = supplierSurveys?.find(
      (survey) => survey.specificationItemId === subItem.id
    );
    if (surveyFromAnswersEndpoint) {
      return {
        status: surveyFromAnswersEndpoint.status,
        description: surveyFromAnswersEndpoint.description ?? "",
      };
    }

    return findSurveyAnswerForItem(supplierCategoriesResponse?.categories, subItem.id);
  }, [
    supplierCategoriesResponse?.categories,
    subItem.id,
    supplierSurveys,
    transientSelectedAnswer,
  ]);

  // Detect conflict: supplier answered but AI answer differs
  const hasConflict = useMemo(() => {
    if (!surveyAnswer || !aiAnswers) return false;
    const aiAnswer = aiAnswers.find((a) => a.specificationItemId === subItem.id);
    if (!aiAnswer) return false;
    const supplierStatus = normalizeAnswerStatus(surveyAnswer.status);
    const aiStatus = normalizeAnswerStatus(aiAnswer.status);
    // Conflict when both have answered but with different statuses
    const answeredStatuses = [
      SpecificationItemAnswerStatus.ANSWERED_YES,
      SpecificationItemAnswerStatus.ANSWERED_NO,
      SpecificationItemAnswerStatus.ANSWERED_PARTIALLY,
    ];
    if (supplierStatus === null || aiStatus === null) return false;
    if (!answeredStatuses.includes(supplierStatus)) return false;
    if (!answeredStatuses.includes(aiStatus)) return false;
    return supplierStatus !== aiStatus;
  }, [surveyAnswer, aiAnswers, subItem.id]);

  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);

  // Description'ı memoize et
  const currentDescription = useMemo(() => {
    return activeSpecificationStep.id === subItem.id
      ? activeSpecificationStep.description
      : subItem.description;
  }, [
    activeSpecificationStep.id,
    activeSpecificationStep.description,
    subItem.id,
    subItem.description,
  ]);

  const isActive = activeSpecificationStep.id === subItem.id;

  useEffect(() => {
    if (isActive && itemRef.current) {
      const element = itemRef.current;
      const rect = element.getBoundingClientRect();
      const container =
        element.closest('[data-sidebar="sidebar"]') ||
        element.closest(".sidebar-content");

      if (container) {
        const containerRect = container.getBoundingClientRect();
        const isVisible =
          rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;

        if (!isVisible) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    }
  }, [isActive]);

  // Overflow kontrolü için stable dependency
  useEffect(() => {
    if (textRef.current) {
      const element = textRef.current;
      const originalWhiteSpace = element.style.whiteSpace;
      element.style.whiteSpace = "nowrap";
      const overflow = element.scrollWidth > element.clientWidth;
      element.style.whiteSpace = originalWhiteSpace;

      setIsOverflowing(overflow);
    }
  }, [currentDescription]);

  const editItem = useCallback(
    (item: ISpecificationItemResponseExtended) => {
      dispatch(setActiveSpecificationItem(item));
    },
    [dispatch]
  );

  return (
    <div
      ref={itemRef}
      data-node-id={subItem.id}
      data-node-type="ITEM"
      data-node-parent-id={item.id}
      data-sidebar="menu-sub-button"
      data-active={isActive}
      className={cn(
        "group/item relative flex items-center gap-2 h-[28px] rounded-none px-2 py-1.5 cursor-pointer transition-all duration-100 border border-transparent",
        getLeafBgClasses({
          isActive,
          hasConflict,
          status: normalizeAnswerStatus(surveyAnswer?.status),
        }),
        !isActive && "hover:border-[#E4E4E7]",
        isActive &&
          "border-[#E4E4E7] shadow-[0_1px_2px_0_rgba(10,13,20,0.03)]"
      )}
      onMouseDown={() => {
        editItem({
          ...subItem,
          category: { id: item.id },
        });
      }}
    >
      {/* Survey Answer Circle - left side */}
      <SurveyAnswerCircle surveyAnswer={surveyAnswer} isActive={isActive} hasConflict={hasConflict} />

      <span
        ref={textRef}
        className={cn(
          "text-xxs font-500 flex-1 whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-200",
          isActive ? "text-[#18181B]" : "text-[#71717A]"
        )}
      >
        {priority}.{subIndex + 1}. {currentDescription}
      </span>

      {isOverflowing && (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="opacity-0 group-hover/item:opacity-100 transition-opacity duration-150"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <ExpandIcon />
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              align="start"
              sideOffset={8}
              className="z-[9999] w-[256px] min-w-[192px] p-3 text-xs font-400 text-zinc-700 leading-[18px] border border-[#E4E4E7] bg-white rounded-lg shadow-md"
            >
              {priority}.{subIndex + 1}. {currentDescription}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

export default CategoryItem;
